import { TranslateManagerService } from './../../service/translate-manager/translate-manager.service';
import {
	HttpHandler,
	HttpRequest,
	HttpEvent,
	HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import { StringHelper } from 'src/app/helper/string/string-helper';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/service/auth/auth.service';
import { Network } from '@awesome-cordova-plugins/network/ngx';
import { Language } from 'src/app/enum/language';
import { App } from '@capacitor/app';
import { environment } from 'src/environments/environment';

@Injectable()
export class ApiInterceptor {

	private disconnected: boolean;
	private appVersion: string = '3.10.94';
	private appBundle: string;

	constructor(
		private authService: AuthService,
		private router: Router,
		private translateManagerService: TranslateManagerService,
		private network: Network
	) {
		this.network.onDisconnect().subscribe(() => this.disconnected = true);
		this.network.onConnect().subscribe(() => {
			this.disconnected = false;
		});
		App
			.getInfo()
			.then(
				value => {
					this.appBundle = value.id;
					this.appVersion = value.version;
				},
				() => {
					if (
						!environment.production &&
						!environment.sandbox &&
						!environment.staging &&
						!environment.preproduction
					) {
						this.appVersion = '10.0.0';
					}
				}
			);
	}

	public intercept(
		httpRequest: HttpRequest<any>,
		httpHandler: HttpHandler
	): Observable<HttpEvent<any>> {
		if (this.disconnected) {
			let message = 'No Internet Connection';
			switch (this.translateManagerService.getCurrentLanguage()) {
				case Language.SPANISH:
					message = 'Sin conexión a Internet';
					break;
				case Language.FRENCH:
					message = 'Pas de Connexion Internet';
					break;
				case Language.PORTUGUESE:
					message = 'Sem Conexão com Internet';
					break;
			}
			// The message will only be displayed in common use of the app. Which has already displayed some previous error message.
			return httpHandler
				.handle(httpRequest)
				.pipe(catchError(() => throwError(() => message)));
		}
		// LANGUAGE
		httpRequest = httpRequest.clone({
			url: httpRequest.url.replace(
				'{language}',
				this.translateManagerService.getCurrentLanguage()
			),
		});
		// JSON
		httpRequest = httpRequest.clone({
			headers: httpRequest.headers.set('Accept', 'application/json'),
		});
		// CURRENT COMPANY
		if (
			httpRequest.url &&
			httpRequest.url.indexOf('{current_company_id}') >= 0
		) {
			if (this.authService.company?.id) {
				httpRequest = httpRequest.clone({
					url: httpRequest.url.replace(
						'{current_company_id}',
						this.authService.company.id.toString()
					),
				});
			} else {
				return EMPTY;
			}
		}
		// AUTH
		const auth: string = this.authService.token;
		if (auth) {
			httpRequest = httpRequest.clone({
				headers: httpRequest.headers.set(
					'Authorization',
					StringHelper.sprintf('Bearer %s', auth)
				),
			});
		}
		// App Version
		if (this.appVersion) {
			httpRequest = httpRequest.clone({
				headers: httpRequest.headers.set(
					'App-Version',
					this.appVersion
				),
			});
		}
		// App Bundle
		if (this.appBundle) {
			httpRequest = httpRequest.clone({
				headers: httpRequest.headers.set(
					'App-Bundle',
					this.appBundle
				),
			});
		}
		return httpHandler
			.handle(httpRequest)
			.pipe(
				tap({
					error: (error) => {
						if (error?.error?.code === 'invalid_company') {
							this.authService.company = null;
							this.router.navigate(['/']);
							return;
						}
						if (error?.error?.code === 'company_suspended') {
							this.router.navigate(['/']);
							return;
						}
						if (
							error instanceof HttpErrorResponse &&
							error.status === 401
						) {
							this.authService.logout();
						}
					}
				}),
				timeout(2 * 60 * 1000) // 2 minutes
			);
	}
}
